import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput, { type OutlinedInputProps } from '@mui/material/OutlinedInput';
import type { ReactNode, Ref } from 'react';

import { type FieldProps, FormField, splitFieldProps, useFormField } from './FormField';

type ControlProps = Omit<
  OutlinedInputProps,
  | 'size'
  | 'error'
  | 'required'
  | 'disabled'
  | 'fullWidth'
  | 'id'
  | 'className'
  | 'sx'
  | 'label'
  | 'ref'
  | 'inputRef'
  // The HTML `prefix` attribute (RDFa) is unused; the name is taken by the slot below.
  | 'prefix'
>;

export interface InputProps extends ControlProps, FieldProps {
  /** Content before the text: an icon or a unit ("R", "https://"). */
  prefix?: ReactNode;
  /** Content after the text: an icon, a unit or a small IconButton. */
  suffix?: ReactNode;
  /** Reaches the `<input>` (or `<textarea>`), so React Hook Form's `register` can focus it. */
  ref?: Ref<HTMLInputElement | HTMLTextAreaElement>;
}

function FieldInput({
  prefix,
  suffix,
  inputProps,
  ref,
  ...props
}: ControlProps & Pick<InputProps, 'prefix' | 'suffix' | 'ref'>) {
  const field = useFormField();
  return (
    <OutlinedInput
      id={field.controlId}
      inputRef={ref}
      startAdornment={
        prefix ? <InputAdornment position="start">{prefix}</InputAdornment> : undefined
      }
      endAdornment={suffix ? <InputAdornment position="end">{suffix}</InputAdornment> : undefined}
      inputProps={{ 'aria-describedby': field.describedBy, ...inputProps }}
      {...props}
    />
  );
}

/** A single-line text field with label, hint and error, as tall as the other controls of its size. */
export function Input(props: InputProps) {
  const { field, control } = splitFieldProps(props);
  return (
    <FormField {...field}>
      <FieldInput {...control} />
    </FormField>
  );
}

export interface TextareaProps extends Omit<InputProps, 'multiline' | 'prefix' | 'suffix'> {
  /** Visible rows before it grows (default 3). One row is exactly the control height. */
  minRows?: number;
  maxRows?: number;
}

/** A multi-line text field; one row matches the control height, more rows grow from there. */
export function Textarea({ minRows = 3, ...props }: TextareaProps) {
  const { field, control } = splitFieldProps(props);
  return (
    <FormField {...field}>
      <FieldInput multiline minRows={minRows} {...control} />
    </FormField>
  );
}
